/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */


import { ScopeQuestionOptionalValue } from '../../../types/surveyScope';
import { MetricGroup } from '../../../types/metricGroup';
import { BlueprintContributions } from '../../../types/survey';
import { InitiativePlain, Materiality, MaterialityMap } from '../../../types/initiative';
import { SDG_CODES } from '../../../constants/utr';
import { TagStatus } from '../../../constants/status';

export const noTagOption = { value: TagStatus.NoTag as string, searchString: 'No Tag', label: 'No Tag' };

export const filterFramework = ({ universalTracker }: Pick<ScopeQuestionOptionalValue, 'universalTracker'>, subCode?: string) => {
  return universalTracker.hasSubTag(subCode);
}

// Filter main and alternatives
export const filterStandard = ({ universalTracker }: Pick<ScopeQuestionOptionalValue, 'universalTracker'>, code: string) => {
  return universalTracker.hasAlternativeInfo(code)
    || universalTracker.getTypeTags().includes(code)
    || universalTracker.getAltTypeTags().includes(code);
}

export const filterStandardAndFramework = (q: Pick<ScopeQuestionOptionalValue, 'universalTracker'>, code: string) => {
  return filterStandard(q, code) || filterFramework(q, code);
}

export const filterQuestionPacks = (q: ScopeQuestionOptionalValue, code: string, metricGroups: MetricGroup[] = []) => {
  return filterStandard(q, code) || filterFramework(q, code) || filterMetricGroups(q, code, metricGroups);
}

export const filterSdg = (question: ScopeQuestionOptionalValue, contributions: BlueprintContributions, code: string) => {
  const utrCode = question.universalTracker.getCode();
  return contributions?.[utrCode]?.includes(`sdg/${code}`);
}

export type MaterialityObject = InitiativePlain['materiality'];

const getSdgUtrMateriality = function (materialityData: MaterialityObject, sdgCode: number) {
  if (!materialityData) {
    return Materiality.None;
  }

  if (materialityData?.[sdgCode] > 66) {
    return Materiality.High;
  } else if (materialityData?.[sdgCode] > 33) {
    return Materiality.Medium
  } else if (materialityData?.[sdgCode] > 0) {
    return Materiality.Low
  }
  return Materiality.None;
};

export const getMaterialityMap = (materialityData?: MaterialityObject, prefix: string = 'sdg/') => {
  const map: MaterialityMap = {
    [Materiality.Low]: [],
    [Materiality.Medium]: [],
    [Materiality.High]: [],
    [Materiality.None]: [],
  }

  if (!materialityData) {
    return map;
  }

  SDG_CODES.forEach((sdgCode) => {
    if (sdgCode !== null) {
      const key = getSdgUtrMateriality(materialityData, sdgCode);
      map[key].push(`${prefix}${sdgCode}`);
    }
  });

  return map
}

// Materialy sdgs
export const filterMateriality = (
  question: ScopeQuestionOptionalValue,
  materialityCode: Materiality,
  materiality: MaterialityMap,
  blueprint: BlueprintContributions,
) => {
  const utrCode = question.universalTracker.getCode();
  const materialityUtrCodes = materiality[materialityCode];
  return blueprint[utrCode]?.some((code) => materialityUtrCodes.includes(code));
}


export const filterUsers = (
  question: ScopeQuestionOptionalValue,
  userId: string,
) => {
  if (question?.utrv?.stakeholders.stakeholder.includes(userId)) {
    return true;
  }
  if (question?.utrv?.stakeholders.verifier.includes(userId)) {
    return true
  }
  return false
}


export const filterQuestionsByRoles = (
  question: ScopeQuestionOptionalValue,
  userId: string
) => {
  return true
}
const objectIdLength = 24;

export const filterMetricGroups = (
  question: Pick<ScopeQuestionOptionalValue, 'universalTracker'>,
  metricGroupId: string,
  metricGroups: Pick<MetricGroup, '_id' | 'universalTrackers'>[] = []
) => {
  if (metricGroupId.length !== objectIdLength) {
    return false;
  }
  const metricGroup = metricGroups.find(g => g._id === metricGroupId);
  return metricGroup?.universalTrackers?.includes(question.universalTracker.getId());
}
